import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, CircularProgress, Switch,
    FormControlLabel, Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { stockCategoryAPI } from '../../services/api';

const INITIAL_FORM = {
    name: '',
    description: '',
    isActive: true,
};

const StockCategoryFormDialog = ({ open, onClose, onSuccess, category }) => {
    const { t } = useTranslation();
    const isEdit = Boolean(category);

    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    // Populate form on edit
    useEffect(() => {
        if (open) {
            if (category) {
                setForm({
                    name: category.name || '',
                    description: category.description || '',
                    isActive: category.isActive !== false,
                });
            } else {
                setForm(INITIAL_FORM);
            }
            setErrors({});
        }
    }, [open, category]);

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) {
            errs.name = t('stockCategories.nameRequired');
        } else if (form.name.trim().length < 2) {
            errs.name = t('stockCategories.nameMinLength');
        } else if (form.name.trim().length > 100) {
            errs.name = t('stockCategories.nameMaxLength');
        }
        if (form.description && form.description.length > 300) {
            errs.description = t('stockCategories.descriptionMaxLength');
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim(),
                isActive: form.isActive,
            };

            if (isEdit) {
                await stockCategoryAPI.update(category._id, payload);
                toast.success(t('stockCategories.updateSuccess'));
            } else {
                await stockCategoryAPI.create(payload);
                toast.success(t('stockCategories.createSuccess'));
            }
            onSuccess();
        } catch (err) {
            const msg = err.response?.data?.message || t('stockCategories.saveError');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {isEdit ? t('stockCategories.editTitle') : t('stockCategories.addTitle')}
            </DialogTitle>

            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={2} pt={1}>
                    {/* Name */}
                    <TextField
                        label={t('stockCategories.name')}
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                        required
                        fullWidth
                        size="small"
                    />

                    {/* Description */}
                    <TextField
                        label={t('common.description')}
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        error={Boolean(errors.description)}
                        helperText={errors.description}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                    />

                    {/* Active Toggle */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.isActive}
                                onChange={handleChange}
                                name="isActive"
                                color="primary"
                            />
                        }
                        label={t('stockCategories.isActive')}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {loading ? t('common.processing') : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StockCategoryFormDialog;